/**
 * useBreakpoint — unified responsive breakpoint hook (ADR-114).
 *
 * Replaces per-app implementations (agent-ui `useResponsive`, ad-hoc window
 * checks) with a single contract:
 * - mobile:   width < 640  (sheet mode boundary)
 * - tablet:   640 <= width < 1024
 * - desktop:  width >= 1024 (fixed navigation boundary)
 *
 * Use CSS/Tailwind variants whenever the behavior is expressible in CSS;
 * this hook is for layout logic that requires JS (mount/unmount, portals).
 */

import { useCallback, useEffect, useState } from 'react';
import { NAV_BREAKPOINT, SHEET_BREAKPOINT } from './constants';

export interface BreakpointState {
  /** Current viewport width in pixels. */
  width: number;
  /** True when width < SHEET_BREAKPOINT (640). */
  isMobile: boolean;
  /** True when SHEET_BREAKPOINT <= width < NAV_BREAKPOINT. */
  isTablet: boolean;
  /** True when width >= NAV_BREAKPOINT (1024). */
  isDesktop: boolean;
  /** True when width < NAV_BREAKPOINT (mobile + tablet). */
  isMobileOrTablet: boolean;
}

function getState(): BreakpointState {
  const width =
    typeof window !== 'undefined' && typeof window.innerWidth === 'number'
      ? window.innerWidth
      : NAV_BREAKPOINT;

  return {
    width,
    isMobile: width < SHEET_BREAKPOINT,
    isTablet: width >= SHEET_BREAKPOINT && width < NAV_BREAKPOINT,
    isDesktop: width >= NAV_BREAKPOINT,
    isMobileOrTablet: width < NAV_BREAKPOINT,
  };
}

export function useBreakpoint(): BreakpointState {
  const [state, setState] = useState<BreakpointState>(getState);

  const handleResize = useCallback(() => {
    setState(getState());
  }, []);

  useEffect(() => {
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [handleResize]);

  return state;
}
